/**
 * PlatformPage — Cinco Platform: doorverwijzingen, commissies en de
 * maandelijkse partnerfactuur (admin-only).
 *
 * Drie weergaven:
 *  - Partners: kaart per externe partner met verdienmodel, deze maand en het
 *    openstaande bedrag. Interne stroom (Kleda) staat apart en gedempt: wel
 *    tellen, geen acties.
 *  - Detail: 12 maanden per partner, met per maand de factuur-run-flow
 *    (preview → guards → bevestigen).
 *  - Runs: alles wat er is gedraaid, met de bijbehorende factuur.
 *
 * Niet te verwarren met /partners — dat is de Kleda-kwartaalregeling met de
 * omgekeerde geldrichting (partner factureert ons).
 *
 * Backend: api/platform.ts. Bevestigen zit achter PLATFORM_FACTURATIE_ENABLED;
 * de preview meldt dat vooraf, de knop is dan uit.
 */
import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  bevestigRun,
  euro,
  haalOverzicht,
  haalPartner,
  haalRunPreview,
  haalRuns,
  nlDatum,
  periodeNaam,
  structuurTekst,
  type CommissieRegel,
  type Factureerbaarheid,
  type Wisseling,
  type PartnerDetail,
  type PartnerMaand,
  type PartnerOverzicht,
  type PartnerOverzichtRij,
  type PartnerStructuur,
  type RunPreview,
  type RunSamenvatting,
  configVerb,
} from '../lib/platform';
import { StructuurDialog, type StructuurModus } from '../components/platform/StructuurDialog';
import { useOfferteDetailDrawerStore } from '../stores/offerte-detail-drawer-store';

type Tab = 'partners' | 'runs';

const STATUS_STIJL: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  gefactureerd: 'bg-emerald-100 text-emerald-800',
  verzonden: 'bg-emerald-100 text-emerald-800',
  vervallen: 'bg-slate-100 text-slate-500',
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-slate-400">—</span>;
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_STIJL[status] ?? 'bg-slate-100 text-slate-700'
      }`}
    >
      {status}
    </span>
  );
}

export function PlatformPage() {
  const [tab, setTab] = useState<Tab>('partners');
  const [overzicht, setOverzicht] = useState<PartnerOverzicht | null>(null);
  const [runs, setRuns] = useState<RunSamenvatting[]>([]);
  const [geselecteerd, setGeselecteerd] = useState<string | null>(null);
  const [detail, setDetail] = useState<PartnerDetail | null>(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  const [nieuwePartner, setNieuwePartner] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);

  const laadOverzicht = useCallback(async () => {
    setLaden(true);
    setFout(null);
    try {
      const [o, r] = await Promise.all([haalOverzicht(12), haalRuns()]);
      setOverzicht(o);
      setRuns(r);
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void laadOverzicht();
  }, [laadOverzicht]);

  const openDetail = useCallback(async (partnerSfId: string) => {
    setGeselecteerd(partnerSfId);
    setDetail(null);
    try {
      setDetail(await haalPartner(partnerSfId, 12));
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // Page-root heeft een eigen scroll-container: AppLayout's <main> is
  // overflow-hidden, anders valt alles onder de fold weg.
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Cinco Platform</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Doorverwijzingen, commissies en de maandelijkse partnerfactuur.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {tab === 'partners' && !geselecteerd && (
              <button
                onClick={() => setNieuwePartner(true)}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Partner toevoegen
              </button>
            )}
            <button
              onClick={() => void laadOverzicht()}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Ververs
            </button>
          </div>
        </div>
        <div className="mt-4 flex gap-1">
          {(['partners', 'runs'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setGeselecteerd(null);
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t === 'partners' ? 'Partners' : `Facturen (${runs.length})`}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        {fout && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {fout}
          </div>
        )}
        {melding && (
          <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {melding}
          </div>
        )}
        {laden && <p className="text-sm text-slate-500">Laden…</p>}

        {!laden && tab === 'partners' && overzicht && !geselecteerd && (
          <PartnerLijst overzicht={overzicht} onOpen={openDetail} />
        )}

        {!laden && tab === 'partners' && geselecteerd && (
          <PartnerDetailPaneel
            partnerSfId={geselecteerd}
            detail={detail}
            onTerug={() => {
              setGeselecteerd(null);
              setDetail(null);
            }}
            onGewijzigd={() => {
              void laadOverzicht();
              void openDetail(geselecteerd);
            }}
          />
        )}

        {!laden && tab === 'runs' && <RunLijst runs={runs} />}
      </div>

      {nieuwePartner && (
        <StructuurDialog
          modus="aanmaken"
          onSluiten={() => setNieuwePartner(false)}
          onKlaar={(m) => {
            setNieuwePartner(false);
            setMelding(m);
            void laadOverzicht();
          }}
        />
      )}
    </div>
  );
}

// ─── Partneroverzicht ───────────────────────────────────────────────────────

function PartnerLijst({
  overzicht,
  onOpen,
}: {
  overzicht: PartnerOverzicht;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Externe partners
        </h2>
        {overzicht.externePartners.length === 0 ? (
          <p className="text-sm text-slate-500">Nog geen externe partners met doorverwijzingen.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {overzicht.externePartners.map((p) => (
              <PartnerKaart key={p.partnerSfId} partner={p} onOpen={onOpen} />
            ))}
          </div>
        )}
      </section>

      {overzicht.interneStroom.length > 0 && (
        <section>
          <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-400">
            Interne stroom
          </h2>
          <p className="mb-3 text-xs text-slate-400">
            Verrekening binnen de eigen groep. Wordt geregistreerd, nooit gefactureerd.
          </p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {overzicht.interneStroom.map((p) => (
              <PartnerKaart key={p.partnerSfId} partner={p} onOpen={onOpen} gedempt />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PartnerKaart({
  partner,
  onOpen,
  gedempt,
}: {
  partner: PartnerOverzichtRij;
  onOpen: (id: string) => void;
  gedempt?: boolean;
}) {
  const dezeMaand = partner.maanden[0];
  return (
    <button
      onClick={() => onOpen(partner.partnerSfId)}
      className={`rounded-lg border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:shadow-sm ${
        gedempt ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-medium text-slate-900">{partner.naam ?? partner.partnerSfId}</h3>
        {gedempt && <span className="text-xs text-slate-400">intern</span>}
      </div>
      <ul className="mt-1.5 space-y-0.5 text-xs text-slate-500">
        {partner.structuren.length === 0 ? (
          <li className="text-amber-700">Geen commissie-structuur ingesteld</li>
        ) : (
          partner.structuren.map((s, i) => <li key={i}>{structuurTekst(s)}</li>)
        )}
      </ul>
      <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-sm">
        <div>
          <dt className="text-xs text-slate-500">Deze maand</dt>
          <dd className="font-medium text-slate-900">{dezeMaand?.doorverwijzingen ?? 0}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Opgepakt</dt>
          <dd className="font-medium text-slate-900">{dezeMaand?.opgepakt ?? 0}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Openstaand</dt>
          <dd className={`font-medium ${partner.totaalOpen > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
            {euro(partner.totaalOpen)}
          </dd>
        </div>
      </dl>
      {partner.laatsteRun && (
        <p className="mt-2 text-xs text-slate-500">
          Laatste factuur: {partner.laatsteRun.invoiceNaam ?? '—'} (
          {periodeNaam(partner.laatsteRun.periodeLabel)})
        </p>
      )}
    </button>
  );
}

// ─── Partnerdetail + run-flow ───────────────────────────────────────────────

function PartnerDetailPaneel({
  partnerSfId,
  detail,
  onTerug,
  onGewijzigd,
}: {
  partnerSfId: string;
  detail: PartnerDetail | null;
  onTerug: () => void;
  onGewijzigd: () => void;
}) {
  const [previewMaand, setPreviewMaand] = useState<string | null>(null);
  const [preview, setPreview] = useState<RunPreview | null>(null);
  const [previewLaden, setPreviewLaden] = useState(false);
  const [previewFout, setPreviewFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [gelukt, setGelukt] = useState<string | null>(null);
  const [openMaand, setOpenMaand] = useState<string | null>(null);
  const [dialoog, setDialoog] = useState<{ modus: StructuurModus; structuur?: PartnerStructuur } | null>(null);

  const openPreview = useCallback(
    async (periode: string) => {
      setPreviewMaand(periode);
      setPreview(null);
      setPreviewFout(null);
      setGelukt(null);
      setPreviewLaden(true);
      try {
        setPreview(await haalRunPreview(partnerSfId, periode));
      } catch (e) {
        setPreviewFout(e instanceof Error ? e.message : String(e));
      } finally {
        setPreviewLaden(false);
      }
    },
    [partnerSfId]
  );

  const doeBevestig = useCallback(async () => {
    if (!previewMaand) return;
    setBezig(true);
    setPreviewFout(null);
    try {
      const r = await bevestigRun(partnerSfId, previewMaand);
      setGelukt(`Factuur ${r.invoiceNaam ?? r.invoiceSfId} aangemaakt.`);
      setPreview(null);
      setPreviewMaand(null);
      onGewijzigd();
    } catch (e) {
      const err = e as Error & { preview?: RunPreview };
      setPreviewFout(err.message);
      if (err.preview) setPreview(err.preview);
    } finally {
      setBezig(false);
    }
  }, [partnerSfId, previewMaand, onGewijzigd]);

  if (!detail) return <p className="text-sm text-slate-500">Partner laden…</p>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={onTerug}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          ← Terug
        </button>
        <h2 className="text-lg font-semibold text-slate-900">{detail.naam ?? partnerSfId}</h2>
        {detail.intern && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
            interne stroom — niet factureerbaar
          </span>
        )}
      </div>

      {gelukt && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {gelukt}
        </div>
      )}

      {!detail.intern && (
        <ContactBlok
          partnerSfId={partnerSfId}
          partnerNaam={detail.naam}
          contact={detail.contact ?? { ccEmail: null, telefoon: null }}
          onGewijzigd={(m) => {
            setGelukt(m);
            onGewijzigd();
          }}
        />
      )}

      {!detail.intern && (
        <VerdienmodelBlok
          partnerSfId={partnerSfId}
          partnerNaam={detail.naam}
          structuren={detail.structuren}
          onActie={(modus, structuur) => setDialoog({ modus, structuur })}
        />
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Maand</th>
              <th className="px-4 py-2 text-right font-medium">Aanvragen</th>
              <th className="px-4 py-2 text-right font-medium">Opgepakt</th>
              <th className="px-4 py-2 text-right font-medium">Opdrachten</th>
              <th className="px-4 py-2 text-right font-medium">Te factureren</th>
              <th className="px-4 py-2 text-right font-medium">Wacht op partner</th>
              <th className="px-4 py-2 text-right font-medium">Gefactureerd</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {detail.maanden.map((m) => {
              const uitgeklapt = openMaand === m.periodeLabel;
              const heeftRegels = m.doorverwijzingen > 0 || m.leadCount > 0 || m.orderCount > 0;
              const regels = detail.commissies.filter((c) => c.periodeLabel === m.periodeLabel);
              return (
                <Fragment key={m.periodeLabel}>
                  <tr
                    className={`${heeftRegels ? 'cursor-pointer hover:bg-slate-50' : ''} ${
                      uitgeklapt ? 'bg-slate-50' : ''
                    }`}
                    onClick={() => heeftRegels && setOpenMaand(uitgeklapt ? null : m.periodeLabel)}
                  >
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-900">
                      {heeftRegels && (
                        <span className="mr-1.5 inline-block w-3 text-slate-400">
                          {uitgeklapt ? '▾' : '▸'}
                        </span>
                      )}
                      {periodeNaam(m.periodeLabel)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-700">{m.doorverwijzingen}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{m.opgepakt}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{m.orderCount}</td>
                    <td className="px-4 py-2 text-right font-medium text-slate-900">
                      {m.openBedrag > 0 ? euro(m.openBedrag) : <span className="font-normal text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-amber-700">
                      {m.wachtBedrag > 0 ? (
                        <>
                          {euro(m.wachtBedrag)}
                          <span className="ml-1 text-xs text-amber-600">({m.wachtCount})</span>
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-700">
                      {m.gefactureerdBedrag > 0 ? (
                        euro(m.gefactureerdBedrag)
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={m.runStatus} />
                      {m.invoiceNaam && (
                        <span className="ml-2 text-xs text-slate-500">{m.invoiceNaam}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!detail.intern && m.openBedrag > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void openPreview(m.periodeLabel);
                          }}
                          className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                        >
                          Factuur-run
                        </button>
                      )}
                    </td>
                  </tr>
                  {uitgeklapt && (
                    <tr>
                      <td colSpan={9} className="bg-slate-50 px-4 pb-4 pt-1">
                        <MaandSpecificatie maand={m} regels={regels} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {previewMaand && (
        <PreviewPaneel
          periode={previewMaand}
          preview={preview}
          laden={previewLaden}
          fout={previewFout}
          bezig={bezig}
          onSluit={() => {
            setPreviewMaand(null);
            setPreview(null);
            setPreviewFout(null);
          }}
          onBevestig={() => void doeBevestig()}
        />
      )}

      {dialoog && (
        <StructuurDialog
          modus={dialoog.modus}
          partnerSfId={partnerSfId}
          partnerNaam={detail.naam}
          structuur={dialoog.structuur}
          onSluiten={() => setDialoog(null)}
          onKlaar={(m) => {
            setDialoog(null);
            setGelukt(m);
            onGewijzigd();
          }}
        />
      )}
    </div>
  );
}

// ─── Contact (cc-adres = E02-ontvanger) ─────────────────────────────────────

/**
 * Waar de partnermail (E02/E04) heen gaat en hoe kantoor de partner belt.
 * Post-cut is dit de enige plek om dat te wijzigen (16-09: Willem paste het
 * adres op de contactpersoon aan, de mail ging naar het oude cc-adres).
 * Schrijft via POST /api/config/partner-contact-wijzigen (guards, dry-run, audit).
 */
function ContactBlok({
  partnerSfId,
  partnerNaam,
  contact,
  onGewijzigd,
}: {
  partnerSfId: string;
  partnerNaam: string | null;
  contact: { ccEmail: string | null; telefoon: string | null };
  onGewijzigd: (melding: string) => void;
}) {
  const [bewerkt, setBewerkt] = useState(false);
  const [ccEmail, setCcEmail] = useState(contact.ccEmail ?? '');
  const [telefoon, setTelefoon] = useState(contact.telefoon ?? '');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    setCcEmail(contact.ccEmail ?? '');
    setTelefoon(contact.telefoon ?? '');
  }, [contact.ccEmail, contact.telefoon]);

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      const body: Record<string, unknown> = { partnerId: partnerSfId };
      const cc = ccEmail.trim();
      const tel = telefoon.trim();
      if (cc !== (contact.ccEmail ?? '')) body.ccEmail = cc === '' ? null : cc;
      if (tel !== (contact.telefoon ?? '')) body.telefoon = tel === '' ? null : tel;
      if (body.ccEmail === null) body.wissenBevestigd = true;
      if (!('ccEmail' in body) && !('telefoon' in body)) {
        setBewerkt(false);
        return;
      }
      const r = await configVerb('partner-contact-wijzigen', body);
      if (!r.success) throw new Error((r as { error?: string }).error ?? 'Opslaan mislukt');
      setBewerkt(false);
      onGewijzigd(`Contact van ${partnerNaam ?? 'de partner'} bijgewerkt: aanvragen gaan naar ${cc || 'geen adres'}.`);
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Opslaan mislukt');
    } finally {
      setBezig(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Contact</h3>
          <p className="text-xs text-slate-500">
            Naar dit adres gaan de aanvragen (partnermail met opgepakt/afgewezen-links).
          </p>
        </div>
        {!bewerkt && (
          <button
            onClick={() => setBewerkt(true)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Wijzigen
          </button>
        )}
      </div>
      {bewerkt ? (
        <div className="space-y-3 px-4 py-3">
          <label className="block text-sm">
            <span className="text-xs font-medium text-slate-600">E-mail voor aanvragen (cc-adres)</span>
            <input
              type="email"
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
              placeholder="bureau@partner.nl"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-slate-600">Telefoon</span>
            <input
              type="tel"
              value={telefoon}
              onChange={(e) => setTelefoon(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
              placeholder="0478 569 750"
            />
          </label>
          {fout && <p className="text-sm text-red-700">{fout}</p>}
          <div className="flex gap-2">
            <button
              onClick={() => void opslaan()}
              disabled={bezig}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {bezig ? 'Opslaan…' : 'Opslaan'}
            </button>
            <button
              onClick={() => {
                setBewerkt(false);
                setFout(null);
                setCcEmail(contact.ccEmail ?? '');
                setTelefoon(contact.telefoon ?? '');
              }}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-2 px-4 py-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-slate-500">E-mail voor aanvragen</dt>
            <dd className={contact.ccEmail ? 'text-slate-900' : 'text-amber-700'}>
              {contact.ccEmail ?? 'geen adres: partner ontvangt geen aanvragen'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Telefoon</dt>
            <dd className="text-slate-900">{contact.telefoon ?? '-'}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}

// ─── Verdienmodel ───────────────────────────────────────────────────────────

/**
 * De commissieafspraken van deze partner, één regel per segment, met de
 * acties eraan. Een partner mag meerdere segmenten doen met per segment een
 * eigen tarief — dat is ook de sleutel waarop de commissie-berekening matcht.
 *
 * Afgesloten afspraken blijven staan (gedempt): ze verklaren waarom oudere
 * commissies een ander bedrag hebben.
 */
function VerdienmodelBlok({
  partnerSfId,
  partnerNaam,
  structuren,
  onActie,
}: {
  partnerSfId: string;
  partnerNaam: string | null;
  structuren: PartnerStructuur[];
  onActie: (modus: StructuurModus, structuur?: PartnerStructuur) => void;
}) {
  const vandaag = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const loopt = (s: PartnerStructuur) =>
    (s.geldigVan == null || s.geldigVan <= vandaag) && (s.geldigTot == null || s.geldigTot >= vandaag);

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Verdienmodel</h3>
          <p className="text-xs text-slate-500">
            Wat {partnerNaam ?? 'deze partner'} ons betaalt, per segment. Bepaalt ook of hij in de inbox
            doorverwijsbaar is.
          </p>
        </div>
        <button
          onClick={() => onActie('aanmaken')}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Segment toevoegen
        </button>
      </div>

      {structuren.length === 0 ? (
        <p className="px-4 py-3 text-sm text-amber-700">
          Geen commissie-structuur ingesteld. Zolang die ontbreekt blijven aanvragen naar deze partner zonder
          bedrag en is hij niet te kiezen bij "Doorsturen naar partner".
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {structuren.map((s) => {
            const actief = loopt(s);
            return (
              <li
                key={s.sfId}
                className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 ${
                  actief ? '' : 'opacity-55'
                }`}
              >
                <div className="text-sm">
                  <span className="font-medium text-slate-900">{structuurTekst(s)}</span>
                  <p className="text-xs text-slate-500">
                    {s.geldigVan ? `vanaf ${nlDatum(s.geldigVan)}` : 'geen ingangsdatum'}
                    {s.geldigTot ? ` t/m ${nlDatum(s.geldigTot)}` : ''}
                    {!actief && ' · niet meer geldig'}
                  </p>
                </div>
                {actief && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onActie('wijzigen', s)}
                      className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Tarief wijzigen
                    </button>
                    <button
                      onClick={() => onActie('beeindigen', s)}
                      className="rounded-md px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-100"
                    >
                      Beëindigen
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
        Wijzigingen gaan via het config-domein naar Salesforce, met dry-run en audit-log. Partner-id{' '}
        {partnerSfId}.
      </p>
    </section>
  );
}

// ─── Specificatie per maand (uitgeklapt onder de maandregel) ────────────────

const FACTUREERBAARHEID_STIJL: Record<Factureerbaarheid, { label: string; klasse: string }> = {
  factureerbaar: { label: 'opgepakt', klasse: 'bg-emerald-100 text-emerald-800' },
  'wacht-op-partner': { label: 'wacht op partner', klasse: 'bg-amber-100 text-amber-800' },
  afgewezen: { label: 'afgewezen', klasse: 'bg-slate-200 text-slate-600' },
};

/**
 * Een aanvraag waar de partner op terugkwam. Rood, want een terugtrekking is
 * dringender dan een gewone afwijzing: de klant rekende al op contact.
 * `wisseling` komt server-side mee, dus dit is presentatie, geen tweede regel.
 */
const WISSELING_STIJL: Record<Wisseling, { label: string; klasse: string }> = {
  teruggetrokken: { label: 'teruggetrokken', klasse: 'bg-rose-100 text-rose-800' },
  'alsnog-opgepakt': { label: 'alsnog opgepakt', klasse: 'bg-emerald-100 text-emerald-800' },
};

/** Telling die tegelijk filter is; actief krijgt een ring zodat het zichtbaar aanstaat. */
function FilterKnop({
  actief,
  onClick,
  klasse,
  children,
}: {
  actief: boolean;
  onClick: () => void;
  klasse: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actief}
      className={`rounded-full px-2 py-0.5 transition hover:bg-slate-100 ${klasse} ${
        actief ? 'bg-slate-100 ring-1 ring-slate-300' : ''
      }`}
    >
      {children}
    </button>
  );
}

/** Leest het label uit "<code>: <label>[ — toelichting]" terug. */
function redenTekst(reden: string | null): string | null {
  if (!reden) return null;
  const na = reden.indexOf(': ');
  return na === -1 ? reden : reden.slice(na + 2);
}

function MaandSpecificatie({
  maand,
  regels,
}: {
  maand: PartnerMaand;
  regels: CommissieRegel[];
}) {
  // null = alles; anders alleen die ene toestand. Tussen 194 rijen is
  // "welke wees hij af" anders niet te vinden.
  const [filter, setFilter] = useState<Factureerbaarheid | null>(null);
  const openOfferte = useOfferteDetailDrawerStore((s) => s.open);

  if (regels.length === 0) {
    return <p className="py-2 text-sm text-slate-500">Geen aanvragen in deze maand.</p>;
  }
  // Opgepakt bovenaan: dat is wat er gefactureerd wordt.
  const volgorde: Record<Factureerbaarheid, number> = {
    factureerbaar: 0,
    'wacht-op-partner': 1,
    afgewezen: 2,
  };
  const zichtbaar = filter ? regels.filter((c) => c.factureerbaarheid === filter) : regels;
  const gesorteerd = [...zichtbaar].sort(
    (a, b) =>
      volgorde[a.factureerbaarheid] - volgorde[b.factureerbaarheid] ||
      b.created_at.localeCompare(a.created_at)
  );

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-100 px-4 py-2 text-xs text-slate-600">
        <span>
          <strong className="text-slate-900">{regels.length}</strong> aanvragen
        </span>
        <FilterKnop
          actief={filter === 'factureerbaar'}
          onClick={() => setFilter(filter === 'factureerbaar' ? null : 'factureerbaar')}
          klasse="text-emerald-700"
        >
          {maand.opgepakt} opgepakt · {euro(maand.openBedrag)} te factureren
        </FilterKnop>
        {maand.wachtCount > 0 && (
          <FilterKnop
            actief={filter === 'wacht-op-partner'}
            onClick={() => setFilter(filter === 'wacht-op-partner' ? null : 'wacht-op-partner')}
            klasse="text-amber-700"
          >
            {maand.wachtCount} wacht op partner · {euro(maand.wachtBedrag)} nog niet
          </FilterKnop>
        )}
        {maand.afgewezen > 0 && (
          <FilterKnop
            actief={filter === 'afgewezen'}
            onClick={() => setFilter(filter === 'afgewezen' ? null : 'afgewezen')}
            klasse="text-slate-500"
          >
            {maand.afgewezen} afgewezen · wordt niet gefactureerd
          </FilterKnop>
        )}
        {filter && (
          <button
            type="button"
            onClick={() => setFilter(null)}
            className="text-slate-400 underline hover:text-slate-600"
          >
            toon alles
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Datum</th>
              <th className="px-4 py-2 text-left font-medium">Referentie</th>
              <th className="px-4 py-2 text-left font-medium">Diensten</th>
              <th className="px-4 py-2 text-left font-medium">Type</th>
              <th className="px-4 py-2 text-left font-medium">Partner</th>
              <th className="px-4 py-2 text-left font-medium">Reden</th>
              <th className="px-4 py-2 text-right font-medium">Bedrag</th>
              <th className="px-4 py-2 text-left font-medium">Facturatie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {gesorteerd.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-3 text-sm text-slate-500">
                  Geen aanvragen in deze selectie.
                </td>
              </tr>
            )}
            {gesorteerd.map((c) => {
              // Kwam de partner erop terug, dan is dát het verhaal van de rij.
              const stijl = c.wisseling
                ? WISSELING_STIJL[c.wisseling]
                : FACTUREERBAARHEID_STIJL[c.factureerbaarheid];
              const telt = c.factureerbaarheid === 'factureerbaar';
              const klikbaar = Boolean(c.leadSfId);
              const reden = redenTekst(c.afgewezen_reden);
              return (
                <tr
                  key={c.id}
                  className={`${telt ? '' : 'text-slate-400'}${klikbaar ? ' cursor-pointer hover:bg-slate-50' : ''}`}
                  onClick={klikbaar ? () => openOfferte(c.leadSfId!) : undefined}
                  title={klikbaar ? 'Open de aanvraag' : undefined}
                >
                  <td className="whitespace-nowrap px-4 py-1.5">{nlDatum(c.created_at)}</td>
                  <td className="px-4 py-1.5">{c.referentie ?? '—'}</td>
                  <td className="px-4 py-1.5">
                    {c.producten.length > 0 ? c.producten.join(', ') : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-1.5">
                    {c.type === 'leadvergoeding' ? 'Aanvraag' : 'Opdracht'}
                  </td>
                  <td className="px-4 py-1.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${stijl.klasse}`}>
                      {stijl.label}
                    </span>
                  </td>
                  <td className="max-w-xs px-4 py-1.5">
                    {c.factureerbaarheid !== 'afgewezen' ? (
                      <span className="text-slate-300">—</span>
                    ) : reden ? (
                      <span title={reden}>{reden}</span>
                    ) : (
                      // Afwijzingen van vóór 02-09 zijn zonder reden vastgelegd.
                      <span className="text-slate-400 italic">niet gevraagd</span>
                    )}
                  </td>
                  <td className={`px-4 py-1.5 text-right ${telt ? 'text-slate-900' : ''}`}>
                    {c.commissie_bedrag == null ? (
                      <span className="text-amber-700">geen bedrag</span>
                    ) : (
                      euro(c.commissie_bedrag)
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    <StatusBadge status={c.facturatie_status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreviewPaneel({
  periode,
  preview,
  laden,
  fout,
  bezig,
  onSluit,
  onBevestig,
}: {
  periode: string;
  preview: RunPreview | null;
  laden: boolean;
  fout: string | null;
  bezig: boolean;
  onSluit: () => void;
  onBevestig: () => void;
}) {
  return (
    <div className="rounded-lg border border-slate-300 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <h3 className="text-base font-semibold text-slate-900">
          Factuurvoorstel {periodeNaam(periode)}
        </h3>
        <button onClick={onSluit} className="text-sm text-slate-500 hover:text-slate-700">
          Sluiten
        </button>
      </div>

      {laden && <p className="mt-3 text-sm text-slate-500">Voorstel berekenen…</p>}
      {fout && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {fout}
        </div>
      )}

      {preview && (
        <>
          {preview.guards.length > 0 && (
            <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-4 py-3">
              <p className="text-sm font-semibold text-red-900">
                Geblokkeerd — eerst oplossen, dan pas factureren
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-red-800">
                {preview.guards.map((g) => (
                  <li key={g.code}>
                    {g.melding}
                    {g.rijIds.length > 0 && (
                      <span className="block text-xs text-red-700">
                        Rij-ids: {g.rijIds.slice(0, 5).join(', ')}
                        {g.rijIds.length > 5 ? ` en ${g.rijIds.length - 5} meer` : ''}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <table className="mt-4 min-w-full divide-y divide-slate-200 text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 text-left font-medium">Omschrijving</th>
                <th className="py-2 text-right font-medium">Aantal</th>
                <th className="py-2 text-right font-medium">Prijs</th>
                <th className="py-2 text-right font-medium">Totaal excl.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {preview.regels.map((r, i) => (
                <tr key={i}>
                  <td className="py-2 pr-4 text-slate-800">{r.omschrijving}</td>
                  <td className="py-2 text-right text-slate-700">{r.aantal}</td>
                  <td className="py-2 text-right text-slate-700">{euro(r.prijsExclBtw)}</td>
                  <td className="py-2 text-right font-medium text-slate-900">
                    {euro(r.aantal * r.prijsExclBtw)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200">
                <td colSpan={3} className="py-2 text-right text-sm text-slate-600">
                  Totaal excl. btw
                </td>
                <td className="py-2 text-right font-semibold text-slate-900">
                  {euro(preview.totaalExclBtw)}
                </td>
              </tr>
            </tfoot>
          </table>

          <p className="mt-2 text-xs text-slate-500">
            {preview.leadCount} opgepakte aanvragen, {preview.orderCount} opdrachten · btw 21% ·
            betalingstermijn 14 dagen · merk Cinco
            {preview.nagekomenCount > 0 &&
              ` · waarvan ${preview.nagekomenCount} nagekomen uit een eerdere maand`}
          </p>

          {(preview.wachtCount > 0 || preview.afgewezenCount > 0) && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
              <span className="font-medium text-slate-700">Niet op deze factuur:</span>{' '}
              {preview.wachtCount > 0 && (
                <>
                  {preview.wachtCount} aanvraag/aanvragen die de partner nog niet oppakte (
                  {euro(preview.wachtBedrag)}) — die komen mee zodra hij ze oppakt
                </>
              )}
              {preview.wachtCount > 0 && preview.afgewezenCount > 0 && '; '}
              {preview.afgewezenCount > 0 && (
                <>
                  {preview.afgewezenCount} afgewezen ({euro(preview.afgewezenBedrag)}) — worden nooit
                  gefactureerd
                </>
              )}
              .
            </div>
          )}

          {preview.eerderGefactureerdCount > 0 && (
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
              <span className="font-medium text-slate-700">Al gefactureerd:</span>{' '}
              {preview.eerderGefactureerdCount} rij(en) uit een eerdere run (
              {euro(preview.eerderGefactureerdBedrag)}). De run kijkt t/m deze maand zodat een laat
              opgepakte aanvraag nakomt; deze rijen zijn al afgerekend en blijven buiten de factuur.
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={onBevestig}
              disabled={!preview.factureerbaar || bezig}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {bezig ? 'Bezig…' : 'Factuur aanmaken en versturen'}
            </button>
            {preview.flagUit && (
              <span className="text-sm text-amber-700">
                Facturatie nog niet vrijgegeven (PLATFORM_FACTURATIE_ENABLED).
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Runs ───────────────────────────────────────────────────────────────────

function RunLijst({ runs }: { runs: RunSamenvatting[] }) {
  if (runs.length === 0) {
    return <p className="text-sm text-slate-500">Nog geen factuur-runs gedraaid.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Partner</th>
            <th className="px-4 py-2 text-left font-medium">Periode</th>
            <th className="px-4 py-2 text-right font-medium">Aanvragen</th>
            <th className="px-4 py-2 text-right font-medium">Opdrachten</th>
            <th className="px-4 py-2 text-right font-medium">Totaal excl.</th>
            <th className="px-4 py-2 text-left font-medium">Status</th>
            <th className="px-4 py-2 text-left font-medium">Factuur</th>
            <th className="px-4 py-2 text-left font-medium">Gedraaid</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {runs.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50">
              <td className="px-4 py-2 text-slate-900">{r.partnerNaam ?? r.partnerSfId}</td>
              <td className="px-4 py-2 text-slate-700">{periodeNaam(r.periodeLabel)}</td>
              <td className="px-4 py-2 text-right text-slate-700">{r.leadCount}</td>
              <td className="px-4 py-2 text-right text-slate-700">{r.orderCount}</td>
              <td className="px-4 py-2 text-right font-medium text-slate-900">
                {euro(r.totaalExclBtw)}
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={r.status} />
              </td>
              <td className="px-4 py-2 text-slate-700">
                {r.invoiceSfId ? (
                  <a
                    href={`/facturen/${r.invoiceSfId}`}
                    className="text-indigo-600 hover:underline"
                  >
                    {r.invoiceNaam ?? 'factuur'}
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-4 py-2 text-slate-600">
                {nlDatum(r.createdAt)}
                {r.createdBy && <span className="block text-xs text-slate-400">{r.createdBy}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PlatformPage;
